import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useUserContext } from "../config/UserContext";
import { useKeycloak } from "../config/KeycloakContext";
import SummaryCard from "../components/SummaryCard";

const DocumentDetailsPageInner: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const { getToken } = useKeycloak();
  const { document, setDocument, freeze, setFreeze, setParcels, state, setState, county, setCounty, entities, setEntities, apiBaseUrl } = useUserContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!documentId || !apiBaseUrl) return;

    let isMounted = true;
    setLoading(true);

    const fetchDetails = async () => {
      try {
        const token = getToken ? await getToken() : null;
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;

        // 1. Fetch document
        let doc = document;
        if (!doc || doc.lookup !== documentId) {
          const docRes = await fetch(`${apiBaseUrl}/api/frozendeed/document/${documentId}`, { headers });
          if (!docRes.ok) throw new Error("Document not found");
          const docData = await docRes.json();
          if (!docData?.data) throw new Error("Document not found");
          doc = docData.data;
          if (isMounted) setDocument(doc);
        }

        // 2. Fetch freeze (property record)
        let p = freeze;
        if (!p || p.id !== doc.freezeId) {
          const parcelRes = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${doc.freezeLookup}`, { headers });
          if (!parcelRes.ok) throw new Error("Property record not found");
          const parcelData = await parcelRes.json();
          if (!parcelData?.data) throw new Error("Property record not found");
          p = parcelData.data;
          if (isMounted) {
            setFreeze(p);
            if (p.parcels) setParcels(p.parcels);
          }
        }

        // 3. Fetch state
        if (!state || state.id !== p.stateId) {
          const stateRes = await fetch(`${apiBaseUrl}/api/states/${p.stateId}`, { headers });
          if (stateRes.ok) {
            const stateData = await stateRes.json();
            if (isMounted) setState(stateData.data);
          }
        }

        // 4. Fetch county
        if (!county || county.id !== p.countyId) {
          const countyRes = await fetch(`${apiBaseUrl}/api/counties/${p.countyId}?all=true`, { headers });
          if (countyRes.ok) {
            const countyData = await countyRes.json();
            if (isMounted) setCounty(countyData.data);
          }
        }

        // 5. Fetch entities
        if (entities.length === 0 || doc.lookup !== document?.lookup) {
          const entityRes = await fetch(`${apiBaseUrl}/api/frozendeed/entities?documentLookup=${doc.lookup}`, { headers });
          if (entityRes.ok) {
            const entityData = await entityRes.json();
            if (isMounted) setEntities(entityData.data);
          }
        }

      } catch (err: any) {
        if (isMounted) setError(err.message || "Failed to load document details");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchDetails();

    return () => { isMounted = false; };
  }, [documentId, apiBaseUrl]);

  if (loading) return <div className="p-8 text-center mt-8">Loading document details...</div>;
  if (error) return <div className="p-8 text-center text-red-500 mt-8">{error}</div>;

  return (
    <div className="flex flex-col items-center w-full bg-white pb-12 pt-8">
      <div className="w-full max-w-[700px] mb-6 flex justify-between items-center px-4 md:px-0">
         <button onClick={() => navigate(-1)} className="text-[#0B4BB6] font-semibold hover:underline">
           &larr; Back to Property Record
         </button>
      </div>
      <div className="bg-white rounded-2xl px-10 py-8 flex flex-col items-center max-w-[700px] w-full border shadow-sm">
        <SummaryCard viewMode={true} />
      </div>
    </div>
  );
};

const DocumentDetailsPage: React.FC = () => <DocumentDetailsPageInner />;

export default DocumentDetailsPage;
